import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/PageMeta';
import { BACKEND_URL } from '../config';

interface LoginProps {
  authenticated: boolean;
}

export default function Login({ authenticated: _authenticated }: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogin = async () => {
    if (!username || !password) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        setError(t('invalid_credentials') as string);
        setLoading(false);
        return;
      }
      const data = await res.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('admin', username);
      navigate('/');
    } catch {
      setError(t('login_error') as string);
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLogin();
  };

  const field = 'w-full h-12 rounded-xl bg-surface border border-line px-4 text-[15px] text-ink placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <>
      <PageMeta title={t('login') as string} />
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
        <div className="w-full max-w-sm">
          <span
            aria-hidden="true"
            className="block w-12 h-12 mx-auto mb-8 bg-ink"
            style={{ WebkitMask: 'url(/nav-icon.png) center / contain no-repeat', mask: 'url(/nav-icon.png) center / contain no-repeat' }}
          />
          <h1 className="text-center font-display font-extrabold tracking-[-0.04em] text-4xl sm:text-5xl leading-none">{t('admin_access')}</h1>
          <p className="text-center text-muted mt-3">{t('sign_in_to_continue')}</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-4">
            <div>
              <label htmlFor="login-username" className="block text-sm font-medium text-zinc-300 mb-1.5">{t('username')}</label>
              <input id="login-username" type="text" value={username} onChange={e => setUsername(e.target.value)} className={field} autoComplete="username" required />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-zinc-300 mb-1.5">{t('password')}</label>
              <input id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className={field} autoComplete="current-password" required />
            </div>
            {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full h-12 rounded-full bg-ink text-base font-semibold hover:bg-primary hover:text-white transition-colors disabled:opacity-60">
              {loading ? t('signing_in') : t('sign_in')}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
